import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const PLAN = z.enum(["basico", "club_pro", "completo"]);
const SOURCE = z.enum(["header", "quota_modal", "locked_module", "pricing", "billing", "admin"]).optional();

async function ctxFor(context: { userId: string; claims: any }) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const email = context.claims?.email as string | undefined;
  if (!email) throw new Error("email_required");
  const origin = getRequestHeader("origin") || "https://panel.retaillemon.com";
  return { admin: supabaseAdmin, userId: context.userId, email, origin };
}

export const getPlanSubscription = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const m = await import("./planBilling.server");
    return m.planStatus(await ctxFor(context));
  });

export const startPlanCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ plan_key: PLAN, interval: z.enum(["month", "year"]).default("month"), source: SOURCE }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./planBilling.server");
    return m.startCheckout(await ctxFor(context), data.plan_key, data.interval, data.source);
  });

export const changePlanSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ action: z.enum(["change", "cancel", "resume"]), plan_key: PLAN.optional(), source: SOURCE, setup_session_id: z.string().startsWith("cs_").max(200).optional() }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./planBilling.server");
    return m.changePlan(await ctxFor(context), data.action, data.plan_key, data.source, data.setup_session_id);
  });

export const startQueryPackCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ tier_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./planBilling.server");
    return m.startPackCheckout(await ctxFor(context), data.tier_id);
  });

export const adminSetupPlanCatalog = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const m = await import("./planBilling.server");
    return m.setupCatalog(await ctxFor(context));
  });
